/**
 * AdminGames — games registry for the two P2 admin toggles:
 *
 *   1. NEW badge (`is_new`)          — every game, inline toggle
 *   2. Card of the Day (`jackpot_cap`, `cap_enabled`)  — Scratch
 *      Card only, inline number input + kill-switch
 *
 * Sibling to `/admin/game-config` (which owns *global* rules like
 * min/max bet, colors, KYC thresholds). This page owns *per-game*
 * flags that don't fit into that global doc.
 *
 * All writes go through PATCH /api/admin/games/{slug} which is
 * whitelisted at the store level — invalid keys are silently
 * dropped, validators surface as 400.
 */
import { useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { AlertCircle, Percent, Sparkles, Ticket } from "lucide-react";

import api from "../../lib/api";
import { extractError } from "../../lib/formatError";
import NewBadge from "../../components/NewBadge";
import PrizeTableEditor from "../../components/admin/PrizeTableEditor";

const SCRATCH_SLUG = "scratch-card";
const RTP_MIN = 88;
const RTP_MAX = 98;

export default function AdminGames() {
  const [games, setGames] = useState([]);
  const [busySlug, setBusySlug] = useState(null);
  const [error, setError] = useState(null);
  // Local pending edits for the scratch-card cap number (so we can
  // debounce blur-save without holding up single-click toggles).
  const [draftCap, setDraftCap] = useState({});
  // Same pattern for the per-color-game RTP inputs — the field is
  // a float and we only fire the PATCH on blur so keystrokes don't
  // spam the API. When the value equals the server-side rtp_percent
  // (post-save), the draft is cleared so React reads back from `g`.
  const [draftRtp, setDraftRtp] = useState({});
  const [prizeEditorOpen, setPrizeEditorOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/games");
      setGames(data.items || []);
    } catch (e) {
      setError(extractError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (slug, body) => {
    setBusySlug(slug);
    try {
      const { data } = await api.patch(`/admin/games/${slug}`, body);
      // Merge the fresh doc back into the row so the UI reflects the
      // authoritative server state (validator normalization etc.).
      setGames((prev) => prev.map((g) => (g.slug === slug ? { ...g, ...data } : g)));
      toast.success(`Saved · ${slug}`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusySlug(null);
    }
  };

  return (
    <div data-testid="admin-games-page">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">
        Games registry
      </div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-1">
        Per-game switches
      </h1>
      <p className="text-sm text-[color:var(--muted)] mb-6 max-w-xl">
        <b>NEW badge</b> is manual only — no auto-expiry. <b>RTP %</b> is the
        target return-to-player for each game; color-guess games derive the
        payout multiplier from num-choices × RTP, and the Scratch Card
        computes RTP live from its weighted prize table. <b>Card of the Day</b>
        {" "}caps 200× jackpots on the Scratch Card per UTC day.
      </p>

      {error && (
        <div
          className="text-xs text-[color:var(--red)] border border-[color:var(--red)] bg-[color:var(--red)]/10 rounded px-3 py-2 mb-3 flex items-center gap-2"
          data-testid="admin-games-error"
        >
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}

      <div className="rounded-xl border border-[color:var(--line)] overflow-hidden" data-testid="admin-games-table">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] border-b border-[color:var(--line)]">
            <tr>
              <Th>Game</Th>
              <Th>Active</Th>
              <Th className="text-center">
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> NEW badge
                </span>
              </Th>
              <Th className="text-center">
                <span className="inline-flex items-center gap-1.5">
                  <Percent className="w-3 h-3" /> RTP
                </span>
              </Th>
              <Th className="text-center">
                <span className="inline-flex items-center gap-1.5">
                  <Ticket className="w-3 h-3" /> Card of the Day
                </span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {games.map((g) => (
              <tr
                key={g.slug}
                className="border-b border-[color:var(--line)] last:border-0 align-top"
                data-testid={`admin-games-row-${g.slug}`}
              >
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="font-display italic text-lg text-[color:var(--ink)]">{g.name}</span>
                    {g.is_new && <NewBadge testid={`admin-games-${g.slug}-preview-badge`} />}
                  </div>
                  <div className="text-[10px] font-mono text-[color:var(--muted)] mt-0.5">{g.slug}</div>
                </Td>
                <Td className="text-xs text-[color:var(--muted)]">
                  {g.active ? "ON" : "OFF"}
                </Td>
                <Td className="text-center">
                  <Toggle
                    on={!!g.is_new}
                    disabled={busySlug === g.slug}
                    onChange={(next) => patch(g.slug, { is_new: next })}
                    testid={`admin-games-${g.slug}-is-new-toggle`}
                  />
                </Td>
                <Td>
                  <RtpCell
                    game={g}
                    draft={draftRtp[g.slug]}
                    onDraft={(v) => setDraftRtp({ ...draftRtp, [g.slug]: v })}
                    onCommit={async () => {
                      const raw = draftRtp[g.slug];
                      if (raw === undefined || raw === "") return;
                      const v = parseFloat(raw);
                      if (Number.isNaN(v) || v === g.rtp_percent) {
                        const cleared = { ...draftRtp }; delete cleared[g.slug]; setDraftRtp(cleared);
                        return;
                      }
                      await patch(g.slug, { rtp_percent: v });
                      const cleared = { ...draftRtp }; delete cleared[g.slug]; setDraftRtp(cleared);
                    }}
                    onOpenPrizeEditor={() => setPrizeEditorOpen(true)}
                    busy={busySlug === g.slug}
                  />
                </Td>
                <Td>
                  {g.slug === SCRATCH_SLUG ? (
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <label className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
                        <span>Cap</span>
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          value={draftCap[g.slug] ?? g.jackpot_cap ?? 10}
                          onChange={(e) => setDraftCap({ ...draftCap, [g.slug]: e.target.value })}
                          onBlur={() => {
                            const raw = draftCap[g.slug];
                            if (raw === undefined || raw === "") return;
                            const v = parseInt(raw, 10);
                            if (Number.isNaN(v) || v === g.jackpot_cap) {
                              const cleared = { ...draftCap }; delete cleared[g.slug]; setDraftCap(cleared);
                              return;
                            }
                            patch(g.slug, { jackpot_cap: v }).then(() => {
                              const cleared = { ...draftCap }; delete cleared[g.slug]; setDraftCap(cleared);
                            });
                          }}
                          className="bet-input w-16 text-xs font-mono text-center"
                          data-testid={`admin-games-${g.slug}-jackpot-cap-input`}
                        />
                      </label>
                      <label className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
                        <span>Enabled</span>
                        <Toggle
                          on={g.cap_enabled !== false}
                          disabled={busySlug === g.slug}
                          onChange={(next) => patch(g.slug, { cap_enabled: next })}
                          testid={`admin-games-${g.slug}-cap-enabled-toggle`}
                        />
                      </label>
                    </div>
                  ) : (
                    <span className="text-[10px] text-[color:var(--muted)] italic">n/a</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mt-4">
        Changes save on click / blur. Audit log lives under <code>/games_audit</code>.
      </p>

      <PrizeTableEditor
        open={prizeEditorOpen}
        initialTable={games.find((g) => g.slug === SCRATCH_SLUG)?.prize_table}
        onClose={() => setPrizeEditorOpen(false)}
        onSaved={(fresh) => {
          setGames((prev) => prev.map((g) => (g.slug === SCRATCH_SLUG ? { ...g, ...fresh } : g)));
        }}
      />
    </div>
  );
}

// --------------------------------------------------------------------------- //
// RtpCell — per-game RTP editor.
//   * Scratch Card: read-only effective RTP pill + "Edit prize table" button.
//   * Color games:  0..100 number input, warn-only banner when outside 88..98.
// --------------------------------------------------------------------------- //

function RtpCell({ game, draft, onDraft, onCommit, onOpenPrizeEditor, busy }) {
  const isScratch = game.slug === SCRATCH_SLUG;
  const effective = game.effective_rtp_percent;
  const showWarn = effective != null && (effective < RTP_MIN || effective > RTP_MAX);
  const pillClass =
    "font-mono px-2 py-0.5 rounded-full text-[11px] " +
    (showWarn
      ? "text-[color:var(--red)] bg-[color:var(--red)]/10 border border-[color:var(--red)]/40"
      : "text-gold bg-gold/10 border border-gold/40");

  if (isScratch) {
    return (
      <div className="flex flex-col items-center gap-2" data-testid={`admin-games-${game.slug}-rtp-cell`}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-widest uppercase text-[color:var(--muted)]">Effective</span>
          <span className={pillClass} data-testid={`admin-games-${game.slug}-rtp-effective`}>
            {effective != null ? `${effective.toFixed(2)}%` : "—"}
          </span>
        </div>
        <button
          onClick={onOpenPrizeEditor}
          className="text-[10px] tracking-widest uppercase text-gold border border-gold/50 rounded-md px-2 h-7 hover:brightness-110 transition"
          data-testid={`admin-games-${game.slug}-prize-table-btn`}
        >
          Edit prize table
        </button>
        {showWarn && <RtpWarn value={effective} />}
      </div>
    );
  }

  // Color-guess games: single RTP % input.
  if (game.rtp_percent == null) {
    // Defensive — shouldn't hit after the seed backfill, but leave a
    // graceful placeholder for game_types we don't handle here.
    return <span className="text-[10px] text-[color:var(--muted)] italic">n/a</span>;
  }

  const draftNum = draft !== undefined && draft !== "" ? parseFloat(draft) : NaN;
  const showLiveWarn = !Number.isNaN(draftNum) && (draftNum < RTP_MIN || draftNum > RTP_MAX);
  const effectiveShown = showLiveWarn ? draftNum : effective;

  return (
    <div className="flex flex-col items-center gap-1" data-testid={`admin-games-${game.slug}-rtp-cell`}>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          step="0.1"
          min={0}
          max={100}
          value={draft ?? game.rtp_percent ?? ""}
          onChange={(e) => onDraft(e.target.value)}
          onBlur={onCommit}
          disabled={busy}
          className="bet-input w-20 text-sm font-mono text-center"
          data-testid={`admin-games-${game.slug}-rtp-input`}
        />
        <span className="text-[11px] text-[color:var(--muted)]">%</span>
      </div>
      <span className="text-[9px] tracking-widest uppercase text-[color:var(--muted)]">
        payout ×
        <span className="ml-1 font-mono text-[color:var(--ink)]">
          {game.payout_multiplier != null ? game.payout_multiplier.toFixed(2) : "—"}
        </span>
      </span>
      {(showLiveWarn || (draft === undefined && showWarn)) && <RtpWarn value={effectiveShown} />}
    </div>
  );
}

function RtpWarn({ value }) {
  return (
    <div
      className="text-[10px] text-[color:var(--red)] border border-[color:var(--red)]/40 bg-[color:var(--red)]/10 rounded px-1.5 py-0.5 max-w-[200px] leading-snug flex items-start gap-1"
      data-testid="rtp-warning-banner"
    >
      <AlertCircle className="w-3 h-3 shrink-0 mt-px" />
      <span>
        Non-standard RTP ({value != null ? value.toFixed(2) : "—"}%). Values
        outside {RTP_MIN}–{RTP_MAX}% may skew economics — double-check before
        saving.
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Toggle — small pill switch. Matches the visual language of other admin
// pages (see AdminGameConfig sliders / AdminSportsbook switches). No shadcn
// dependency because the admin console already renders on the legacy CSS
// vars (`--ink` etc.).
// --------------------------------------------------------------------------- //

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      data-testid={testid}
      data-state={on ? "on" : "off"}
      className={
        "relative inline-flex items-center h-5 w-9 rounded-full transition " +
        "border " +
        (on
          ? "bg-gradient-to-r from-[#ff3d3d] to-[#ff9138] border-transparent shadow-[0_0_10px_rgba(255,80,60,0.45)]"
          : "bg-[color:var(--shell-2)] border-[color:var(--line)] ") +
        (disabled ? " opacity-50 cursor-not-allowed" : " hover:brightness-110 cursor-pointer")
      }
    >
      <span
        className={
          "inline-block w-3.5 h-3.5 rounded-full bg-white shadow transition-transform " +
          (on ? "translate-x-4" : "translate-x-1")
        }
      />
    </button>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-4 py-3 font-normal ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
